/**
 * 학원 도메인 API.
 * 기존 관리자 api client 재사용 (토큰 자동 첨부 + 401 시 /login 리다이렉트).
 *
 * storeId 전달 규칙:
 *   - manager/staff: 보내지 않는다. backend(resolveStoreId)가 본인 매장으로 강제하므로
 *     지정할 필요가 없고, 지정해도 무시된다.
 *   - master: 현재 선택한 매장의 storeId 를 GET params / POST·PATCH body 로 명시 전달해야 한다
 *     (resolveStoreId 가 master 에겐 storeId 를 필수로 요구). 화면은 modules/academy/scope.js 의
 *     academyScope(session) 로 이 값을 만들어 넘긴다.
 */
import api from '../../api/client';

export async function fetchClasses(params = {}) {
  const res = await api.get('/academy/classes', { params });
  return res.data;
}

export async function createClass(payload) {
  const res = await api.post('/academy/classes', payload);
  return res.data;
}

export async function updateClass(id, payload) {
  const res = await api.patch(`/academy/classes/${id}`, payload);
  return res.data;
}

export async function fetchStudents(params = {}) {
  const res = await api.get('/academy/students', { params });
  return res.data;
}

export async function createStudent(payload) {
  const res = await api.post('/academy/students', payload);
  return res.data;
}

export async function updateStudent(id, payload) {
  const res = await api.patch(`/academy/students/${id}`, payload);
  return res.data;
}

/**
 * 보호자 원문 연락처/메모 상세.
 * master/manager + parents.manage 전용이며 backend가 응답 전에 열람 감사를 남긴다.
 */
export async function fetchStudentContact(id, params = {}) {
  const res = await api.get(`/academy/students/${id}/contact`, { params });
  return res.data;
}
export async function fetchTuitionBillings(params = {}) {
  const res = await api.get('/academy/tuition-billings', { params });
  return res.data;
}

export async function registerTuitionBilling(payload) {
  const res = await api.post('/academy/tuition-billings', payload);
  return res.data;
}

/** 청구 이력 (성공·실패 모두) */
export async function fetchTuitionPayments(params = {}) {
  const res = await api.get('/academy/tuition-payments', { params });
  return res.data;
}

export async function cancelTuitionBilling(id, body = {}) {
  const res = await api.patch(`/academy/tuition-billings/${id}/cancel`, body);
  return res.data;
}

export async function fetchAcademySummary(params = {}) {
  const res = await api.get('/academy/summary', { params });
  return res.data;
}

/** 실청구를 열기 전에 충족해야 하는 법무·동의·첫 가맹점 게이트 */
export async function fetchAcademyBillingReadiness() {
  const res = await api.get('/academy/billing-readiness');
  return res.data;
}

/** 중도 퇴원 환불 계산 미리보기. 실제 Toss 환불을 실행하지 않는다. */
export async function createAcademyRefundQuote(payload) {
  const res = await api.post('/academy/refund-quotes', payload);
  return res.data;
}

/** 환불 견적을 승인 대기 상태로 기록한다. 실제 Toss 환불을 실행하지 않는다. */
export async function createAcademyRefundRequest(payload) {
  const res = await api.post('/academy/refund-requests', payload);
  return res.data;
}

export async function fetchAcademyRefundRequests(params = {}) {
  const res = await api.get('/academy/refund-requests', { params });
  return res.data;
}

/** 승인/반려는 감사 가능한 내부 결정 기록이며 실제 환불 실행이 아니다. */
export async function decideAcademyRefundRequest(id, decision, payload = {}) {
  const endpoint = decision === 'approved' ? 'approve' : 'reject';
  const res = await api.patch(`/academy/refund-requests/${id}/${endpoint}`, payload);
  return res.data;
}

export async function fetchAcademySiblingDiscountPolicy(params = {}) {
  const res = await api.get('/academy/sibling-discount-policy', { params });
  return res.data;
}

/** 형제 할인 계산 미리보기. 현재 자동결제 금액에는 반영되지 않는다. */
export async function previewAcademySiblingDiscount(payload) {
  const res = await api.post('/academy/sibling-discount-preview', payload);
  return res.data;
}

// ── 출결 ──────────────────────────────────────────────

/** 반+날짜 출결 현황 (미기록 원생은 status='unmarked' 로 온다) */
export async function fetchAttendance(params = {}) {
  const res = await api.get('/academy/attendance', { params });
  return res.data;
}

/** 출결 저장 (여러 원생 원자적 upsert) → 갱신된 현황을 반환 */
export async function saveAttendance(payload) {
  const res = await api.post('/academy/attendance', payload);
  return res.data;
}

// ── 학부모(보호자) ────────────────────────────────────

/** 보호자 목록 (원생 보호자 정보에서 파생, 전화번호는 마스킹) */
export async function fetchGuardians(params = {}) {
  const res = await api.get('/academy/guardians', { params });
  return res.data;
}

/** 매장의 보호자 앱 연결 요청 상태 목록 (manager 이상) */
export async function fetchGuardianLinkRequests(params = {}) {
  const res = await api.get('/academy/guardian-link-requests', { params });
  return res.data;
}

/** 보호자 앱 연결 '요청' 생성 (manager 이상). 회원 검색결과/ID는 반환되지 않는다. */
export async function createGuardianLinkRequest(payload) {
  const res = await api.post('/academy/guardian-link-requests', payload);
  return res.data;
}

/** 진행 중(pending)인 연결 요청 취소 (manager 이상, 재요청 전 정리) */
export async function cancelGuardianLinkRequest(id, body = {}) {
  const res = await api.patch(`/academy/guardian-link-requests/${id}/cancel`, body);
  return res.data;
}
